
import { pricingComparison } from '../../../mocks/comparisonData';

function formatPrice(n: number) {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export default function PricingBreakdown() {
  const { vehicleExample, dealership, broker, savings } = pricingComparison;

  return (
    <section className="py-20 px-4 sm:px-6 bg-white">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block text-teal-600 text-xs font-semibold tracking-wider uppercase mb-3">
            Real Numbers, Real Savings
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            <a href="#pricing" className="hover:text-teal-600 transition-colors">
              The Real Cost Comparison
            </a>
          </h2>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto">
            Based on a <strong>{vehicleExample}</strong> — see exactly where your money goes at a dealership
            like Warrenton Kia or Lums Toyota versus Rockhound Auto.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 items-start">
          {/* Dealership Column */}
          <div className="rounded-xl border-2 border-red-200 bg-red-50/30 overflow-hidden shadow-md">
            <div className="bg-red-100 px-6 py-4 text-center">
              <span className="text-xs font-semibold text-red-700 uppercase tracking-wider">Traditional Dealership</span>
              <div className="text-3xl font-bold text-red-700 mt-1">{formatPrice(dealership.total)}</div>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#374151]">Base Vehicle Price</span>
                <span className="font-medium text-gray-900">{formatPrice(dealership.basePrice)}</span>
              </div>
              <div className="flex justify-between text-sm bg-red-50 -mx-2 px-2 py-1.5 rounded">
                <span className="text-red-700 font-medium">Dealer Markup</span>
                <span className="font-semibold text-red-700">+{formatPrice(dealership.dealerMarkup)}</span>
              </div>
              <div className="flex justify-between text-sm bg-red-50 -mx-2 px-2 py-1.5 rounded">
                <span className="text-red-700 font-medium">Destination Fee</span>
                <span className="font-semibold text-red-700">+{formatPrice(dealership.destinationFee)}</span>
              </div>
              <div className="flex justify-between text-sm bg-red-50 -mx-2 px-2 py-1.5 rounded">
                <span className="text-red-700 font-medium">Dealer Prep Fee</span>
                <span className="font-semibold text-red-700">+{formatPrice(dealership.prepFee)}</span>
              </div>
              <div className="flex justify-between text-sm bg-red-50 -mx-2 px-2 py-1.5 rounded">
                <span className="text-red-700 font-medium">Advertising Fee</span>
                <span className="font-semibold text-red-700">+{formatPrice(dealership.advertisingFee)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#374151]">Documentation Fee</span>
                <span className="font-medium text-gray-900">+{formatPrice(dealership.docFee)}</span>
              </div>
              <div className="border-t border-red-200 pt-3 mt-3 flex justify-between">
                <span className="text-sm font-bold text-gray-900">Total Out-the-Door</span>
                <span className="text-lg font-bold text-red-700">{formatPrice(dealership.total)}</span>
              </div>
            </div>
          </div>

          {/* Broker Column */}
          <div className="rounded-xl border-2 border-teal-200 bg-teal-50/30 overflow-hidden shadow-md relative">
            <div className="absolute -top-0 right-4 bg-teal-700 text-white text-xs font-bold px-3 py-1 rounded-b-lg">
              BEST VALUE
            </div>
            <div className="bg-teal-100 px-6 py-4 text-center">
              <span className="text-xs font-semibold text-teal-800 uppercase tracking-wider">Rockhound Auto Broker</span>
              <div className="text-3xl font-bold text-teal-800 mt-1">{formatPrice(broker.total)}</div>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#374151]">Wholesale Vehicle Price</span>
                <span className="font-medium text-gray-900">{formatPrice(broker.basePrice)}</span>
              </div>
              <div className="flex justify-between text-sm bg-teal-50 -mx-2 px-2 py-1.5 rounded">
                <span className="text-teal-800 font-medium">Transparent Broker Fee</span>
                <span className="font-semibold text-teal-800">+{formatPrice(broker.brokerFee)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#374151]">Documentation Fee</span>
                <span className="font-medium text-gray-900">+{formatPrice(broker.docFee)}</span>
              </div>
              <div className="flex justify-between text-sm bg-teal-50 -mx-2 px-2 py-1.5 rounded">
                <span className="text-teal-800 font-medium flex items-center gap-1">
                  <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>Hidden Fees
                </span>
                <span className="font-semibold text-teal-800">$0</span>
              </div>
              <div className="flex justify-between text-sm bg-teal-50 -mx-2 px-2 py-1.5 rounded">
                <span className="text-teal-800 font-medium flex items-center gap-1">
                  <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>Dealer Markup
                </span>
                <span className="font-semibold text-teal-800">$0</span>
              </div>
              <div className="flex justify-between text-sm bg-teal-50 -mx-2 px-2 py-1.5 rounded">
                <span className="text-teal-800 font-medium flex items-center gap-1">
                  <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>Pressure Tactics
                </span>
                <span className="font-semibold text-teal-800">$0</span>
              </div>
              <div className="border-t border-teal-200 pt-3 mt-3 flex justify-between">
                <span className="text-sm font-bold text-gray-900">Total Out-the-Door</span>
                <span className="text-lg font-bold text-teal-800">{formatPrice(broker.total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Savings Banner */}
        <div className="mt-8 bg-gradient-to-r from-teal-600 to-teal-700 rounded-xl p-6 sm:p-8 text-center text-white shadow-lg">
          <p className="text-sm font-medium text-teal-100 mb-1">Your Total Savings</p>
          <p className="text-4xl sm:text-5xl font-bold mb-2">{formatPrice(savings)}</p>
          <p className="text-sm text-teal-100 max-w-lg mx-auto">
            On this single vehicle alone. Imagine the savings across Warrenton, Astoria, Seaside, and
            the entire Pacific Northwest — that\'s money back in your pocket, not the dealership\'s.
          </p>
        </div>
      </div>
    </section>
  );
}
